import { useCallback, useMemo, useState } from "react";

import type { ProviderOptions, ProviderResource, UpdateProviderOptions } from "@/api/import/providers";

// Providers have a fixed shape, so the form is plain local state rather than the
// server-generated field list the vendored resources (sources, destinations) use.
export type ProviderDraft = {
  name: string;
  id: string;
  datalake_id: string;
  products: string[];
};

export const EMPTY_PROVIDER_DRAFT: ProviderDraft = {
  name: "",
  id: "",
  datalake_id: "",
  products: [],
};

export const PROVIDER_ID_MAX_LENGTH = 64;

export const providerToDraft = (provider: ProviderResource): ProviderDraft => ({
  name: provider.name,
  id: provider.id,
  datalake_id: provider.datalake_id,
  products: [...provider.products],
});

export type UseProviderFormResult = {
  draft: ProviderDraft;
  setField: <K extends keyof ProviderDraft>(key: K, value: ProviderDraft[K]) => void;
  toggleProduct: (product: string, checked: boolean) => void;
  isValid: boolean;
  getProvider: () => ProviderOptions;
  getProviderUpdate: () => UpdateProviderOptions;
};

export function useProviderForm(initialDraft: ProviderDraft = EMPTY_PROVIDER_DRAFT): UseProviderFormResult {
  const [draft, setDraft] = useState<ProviderDraft>(initialDraft);

  const setField = useCallback(<K extends keyof ProviderDraft>(key: K, value: ProviderDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  }, []);

  const toggleProduct = useCallback((product: string, checked: boolean) => {
    setDraft((current) => ({
      ...current,
      products: checked ? [...current.products, product] : current.products.filter((p) => p !== product),
    }));
  }, []);

  // The API requires at least one product on both create and update.
  const isValid = useMemo(
    () => !!draft.name.trim() && !!draft.id.trim() && !!draft.datalake_id && draft.products.length > 0,
    [draft],
  );

  // New providers are enabled on creation; the Admin tab owns disabling them later.
  const getProvider = useCallback(
    (): ProviderOptions => ({
      name: draft.name.trim(),
      id: draft.id.trim(),
      datalake_id: draft.datalake_id,
      products: draft.products,
      is_enabled: true,
    }),
    [draft],
  );

  // ID and datalake are immutable, so an edit only submits what the API accepts.
  const getProviderUpdate = useCallback(
    (): UpdateProviderOptions => ({
      name: draft.name.trim(),
      products: draft.products,
    }),
    [draft],
  );

  return { draft, setField, toggleProduct, isValid, getProvider, getProviderUpdate };
}
